import React from 'react';
import {
  Users, FileText, CheckCircle, Sprout, TrendingUp, Clock, Shield, ArrowRight, DollarSign, Calendar, CreditCard, ShoppingCart,
  Landmark, 
  ArrowRightCircle, 
  Percent, 
  RefreshCcw, 
  Wheat 
} from "lucide-react";
import { useLanguage } from '../context/LanguageContext';

export default function Kcc() {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "Kisan Credit Card (KCC)",
      overview: "Overview",
      overviewText: "The Kisan Credit Card (KCC) facility through Cooperative Societies provides farmers with easy, flexible, and timely access to credit for their cultivation needs. It ensures that farmers can purchase inputs, manage seasonal expenses, and handle unforeseen requirements without financial stress.",
      
      keyFeatures: "Key Features",
      features: [
        'Hassle-free access to short-term agricultural loans',
        'Quick processing through Primary Agricultural Cooperative Credit Societies (PACCS)',
        'Affordable interest rates with government interest subvention',
        'Flexible withdrawal and repayment options',
        'Coverage for crops, livestock, allied activities'
      ],
      
      whoCanApply: "Who Can Apply?",
      applicantCategories: [
        {
          title: 'Small & Marginal Farmers',
          desc: 'Farmers with small landholdings seeking credit support'
        },
        {
          title: 'Tenant Farmers & Sharecroppers',
          desc: 'Agricultural workers cultivating leased land'
        },
        {
          title: 'Allied Activities Farmers',
          desc: 'Engaged in crop production, dairy, poultry, fisheries'
        }
      ],
      
      processFlow: "Process Flow",
      processSteps: [
        "Application at PACCS",
        "Land & Eligibility Verification",
        "Credit Limit Assessment",
        "KCC Issued",
        "Amount Drawal",
      ],
      
      benefits: "Benefits to Farmers",
      benefitsList: [
        "Reduces dependency on informal lenders",
        "Ensures timely purchase of seeds, fertilisers, pesticides",
        "Supports recurring and seasonal expenses",
        "Simplifies credit access through a single card",
      ],
      benefitsAlt: "Farmer Benefits"
    },
    ta: {
      title: "கிசான் கடன் அட்டை (KCC)",
      overview: "கண்ணோட்டம்",
      overviewText: "கூட்டுறவு சங்கங்கள் மூலம் கிசான் கடன் அட்டை (KCC) வசதி விவசாயிகளுக்கு அவர்களின் பயிர்ச்செய்கை தேவைகளுக்கு எளிதான, நெகிழ்வான மற்றும் சரியான நேரத்தில் கடன் அணுகலை வழங்குகிறது. விவசாயிகள் உள்ளீடுகளை வாங்கவும், பருவகால செலவுகளை நிர்வகிக்கவும், எதிர்பாராத தேவைகளை நிதி மன அழுத்தம் இல்லாமல் கையாளவும் இது உறுதி செய்கிறது.",
      
      keyFeatures: "முக்கிய அம்சங்கள்",
      features: [
        'குறுகிய கால விவசாய கடன்களுக்கு எளிதான அணுகல்',
        'முதன்மை விவசாய கூட்டுறவு கடன் சங்கங்கள் (PACCS) மூலம் விரைவான செயலாக்கம்',
        'அரசு வட்டி மானியத்துடன் மலிவு வட்டி விகிதங்கள்',
        'நெகிழ்வான திரும்பப் பெறுதல் மற்றும் திருப்பிச் செலுத்தும் விருப்பங்கள்',
        'பயிர்கள், கால்நடைகள், தொடர்புடைய நடவடிக்கைகளுக்கான கவரேஜ்'
      ],
      
      whoCanApply: "யார் விண்ணப்பிக்கலாம்?",
      applicantCategories: [
        {
          title: 'சிறு மற்றும் குறு விவசாயிகள்',
          desc: 'கடன் ஆதரவு தேடும் சிறிய நிலப்பரப்புகளைக் கொண்ட விவசாயிகள்'
        },
        {
          title: 'குத்தகை விவசாயிகள் மற்றும் பங்கு பயிரிடுபவர்கள்',
          desc: 'குத்தகை நிலத்தில் பயிரிடும் விவசாய தொழிலாளர்கள்'
        },
        {
          title: 'தொடர்புடைய செயல்பாடுகள் விவசாயிகள்',
          desc: 'பயிர் உற்பத்தி, பால், கோழி வளர்ப்பு, மீன்வளர்ப்பு ஆகியவற்றில் ஈடுபட்டுள்ளவர்கள்'
        }
      ],
      
      processFlow: "செயல்முறை பாய்வு",
      processSteps: [
        "PACCS இல் விண்ணப்பம்",
        "நிலம் மற்றும் தகுதி சரிபார்ப்பு",
        "கடன் வரம்பு மதிப்பீடு",
        "KCC வழங்கப்பட்டது",
        "தொகை திரும்பப் பெறுதல்",
      ],
      
      benefits: "விவசாயிகளுக்கான நன்மைகள்",
      benefitsList: [
        "முறைசாரா கடன் வழங்குபவர்களைச் சார்ந்திருப்பதைக் குறைக்கிறது",
        "விதைகள், உரங்கள், பூச்சிக்கொல்லிகளை சரியான நேரத்தில் வாங்குவதை உறுதி செய்கிறது",
        "மீண்டும் வரும் மற்றும் பருவகால செலவுகளை ஆதரிக்கிறது",
        "ஒற்றை அட்டை மூலம் கடன் அணுகலை எளிதாக்குகிறது",
      ],
      benefitsAlt: "விவசாயி நன்மைகள்"
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Top Page Header */}
      <div className="w-full bg-white py-6 sm:py-8">
        <h1 className="text-center text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 px-4">
          {t.title}
        </h1>
      </div>
      
      <div className="w-full px-4 sm:px-6 md:px-8 py-8 sm:py-12">
        
        {/* Overview Section */}
        <div className="bg-blue-50 rounded-xl p-4 sm:p-6 md:p-8 mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-3">
            {t.overview}
          </h2>
          <div className="border-l-4 border-blue-600 pl-4 sm:pl-6">
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              {t.overviewText}
            </p>
          </div>
        </div>

        {/* Key Features Section - Card Style */}
        <div className="mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 px-0 sm:px-4 md:px-8">{t.keyFeatures}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              { icon: Landmark, color: 'bg-blue-600' },
              { icon: ArrowRightCircle, color: 'bg-sky-500' },
              { icon: Percent, color: 'bg-gray-600' },
              { icon: RefreshCcw, color: 'bg-blue-600' },
              { icon: Wheat, color: 'bg-sky-500' }
            ].map((feature, index) => (
              <div key={index} className={`${feature.color} rounded-xl p-4 sm:p-6 text-white shadow-lg`}>
                <div className="bg-white bg-opacity-20 w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                  <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <p className="text-base sm:text-lg leading-snug">{t.features[index]}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Who Can Apply Section - Enhanced Card Grid */}
        <div className="mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-12 text-center text-gray-900 px-4">
            {t.whoCanApply.split(' ').slice(0, -1).join(' ')}{' '}
            <span className="text-blue-600">{t.whoCanApply.split(' ').slice(-1)}</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[
              { icon: Users, color: 'bg-blue-50 border-blue-200' },
              { icon: Users, color: 'bg-purple-50 border-purple-200' },
              { icon: Sprout, color: 'bg-orange-50 border-orange-200' }
            ].map((category, index) => (
              <div key={index} className={`${category.color} border-2 rounded-xl p-4 sm:p-6 md:p-8 hover:shadow-lg transition-shadow`}>
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <div className="bg-white p-2 sm:p-3 rounded-lg shadow-sm">
                    <category.icon className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-base sm:text-lg">{t.applicantCategories[index].title}</h3>
                </div>
                <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed">{t.applicantCategories[index].desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Process Flow - Stepper Box Flow */}
        <div className="mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">{t.processFlow}</h2>

          <div className="relative">
            {/* Horizontal Dotted Line - Hidden on mobile */}
            <div className="hidden lg:block absolute top-6 left-0 w-full border-t-2 border-dashed border-gray-400 z-0"></div>

            {/* Steps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 relative z-10">
              {t.processSteps.map((step, index) => (
                <div key={index} className="flex flex-col items-center text-center relative">
                  
                  {/* Step Number */}
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mb-2 sm:mb-3 text-sm sm:text-base">
                    {index + 1}
                  </div>

                  {/* Step Card */}
                  <div className="bg-blue-100 rounded-xl shadow-md p-3 sm:p-4 w-full">
                    <p className="text-gray-700 text-sm sm:text-base md:text-lg font-medium">{step}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Benefits Section - Image Left, Icons on Right */}
        <div className="mb-8 sm:mb-16 flex flex-col lg:flex-row items-stretch gap-6 sm:gap-8 lg:gap-10 bg-gray-100 p-4 sm:p-6 md:p-8 rounded-xl">
          {/* Left Image */}
          <div className="lg:w-1/2 flex justify-center">
            <img
              src="/Images/TVM Region (2).jpg"
              alt={t.benefitsAlt}
              className="w-full rounded-xl shadow-lg object-cover h-64 sm:h-80 lg:h-full"
            />
          </div>

          {/* Right Content */}
          <div className="lg:w-1/2 flex flex-col justify-between">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 md:mb-8">{t.benefits}</h2>
            <ul className="space-y-3 sm:space-y-4 md:space-y-6 flex-1 flex flex-col justify-between list-disc list-inside">
              {t.benefitsList.map((benefit, index) => (
                <li key={index} className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
}